import React, { memo } from 'react';
import PropTypes from 'prop-types';
import MuiButton from '@mui/material/Button';
import { lighten } from '@mui/material/styles';

import { makeStyles } from 'src/makeStyles';

import { CircularProgress } from '@mui/material';
import { colors } from '../../constants/styleConstants';
import { BUTTON_VARIANTS, TEXT_TRANSFORM } from '../../constants/consts';

const useStyles = makeStyles()((theme, props) => ({
  button: {
    [theme.breakpoints.up('sm')]: {
      padding: '10px 12px',
      fontSize: 16,
      fontWeight: 'bold',
      lineHeight: '18px',
      borderRadius: 4,
      textTransform: props?.textTransform,
      '& .MuiSvgIcon-root': {
        height: 18,
      },
    },
  },
  primary: {
    background: colors.blue3,
    color: colors.white,
    border: `1px solid ${colors.blue4}`,
    '&:hover': {
      background: colors.blue3,
      color: colors.white,
      boxShadow: '0px 4px 4px rgba(0, 50, 96, 0.2)',
      border: `1px solid ${colors.blue4}`,
    },
    '&:active': {
      background: colors.blue3,
      color: colors.white,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: colors.white,
      background: lighten(colors.blue3, 0.4),
      border: '1px solid rgba(0, 80, 153, 0.3)',
      '&:hover': {
        color: colors.white,
        background: lighten(colors.blue3, 0.4),
        border: '1px solid rgba(0, 80, 153, 0.3)',
      },
    },
  },
  secondary: {
    background: colors.white,
    color: colors.grey3,
    border: `1px solid ${colors.grey6}`,
    '&:hover': {
      background: colors.white,
      color: colors.grey3,
      boxShadow: `0px 4px 4px ${colors.blackOpacity15}`,
      border: `1px solid ${colors.grey6}`,
    },
    '&:active': {
      background: colors.white,
      color: colors.grey3,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: lighten(colors.grey3, 0.4),
      background: colors.white,
      border: `1px solid ${colors.grey6}`,
      '&:hover': {
        color: lighten(colors.grey3, 0.4),
        background: colors.white,
        border: `1px solid ${colors.grey6}`,
      },
    },
  },
  tertiary: {
    background: colors.blue3Opacity22,
    color: colors.blue3,
    border: `1px solid ${colors.blue3}`,
    '&:hover': {
      background: colors.blue3Opacity22,
      color: colors.blue3,
      boxShadow: `0px 0px 4px ${colors.blue3Opacity31}`,
      border: `1px solid ${colors.blue3}`,
    },
    '&:active': {
      background: colors.blue3Opacity22,
      color: colors.blue3,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: colors.blue5,
      background: colors.blue3Opacity10,
      border: `1px solid ${colors.blue3Opacity50}`,
      '&:hover': {
        color: colors.blue5,
        background: colors.blue3Opacity10,
        border: `1px solid ${colors.blue3Opacity50}`,
      },
    },
  },
  progress: {
    color: props?.variant === 'contained' ? colors.white : colors.blue3,
    marginRight: theme.spacing(2),
    position: 'absolute',
  },
}));

const Button = ({ children, className, styles, variant, color, disabled, isLoading, textTransform, ...rest }) => {
  const { classes, cx } = useStyles({ variant, textTransform });

  return (
    <MuiButton
      variant="outlined"
      disabled={disabled || isLoading}
      className={cx(classes.button, className, {
        [classes.primary]: variant === 'primary',
        [classes.secondary]: variant === 'secondary',
        [classes.tertiary]: variant === 'tertiary',
      })}
      {...rest}
    >
      {isLoading && <CircularProgress className={classes.progress} size={15} />}
      {children}
    </MuiButton>
  );
};

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  textTransform: PropTypes.string,
};

Button.defaultProps = {
  variant: BUTTON_VARIANTS.PRIMARY,
  isLoading: false,
  disabled: false,
  textTransform: TEXT_TRANSFORM.UPPER_CASE,
};

export default memo(Button);
